// changeJoinKind — VQE-Edit edit-action: change a JOIN clause's kind keyword
// (e.g. INNER ↔ LEFT, INNER → CROSS) via SR-Edit's `modify-join-kind` op.
// Builds the StructuralEdit and delegates to `commitEdit`. The keyword bytes
// are replaced in place; user casing is preserved (SR-Edit). Converting to a
// CROSS-style kind drops any ON predicate; the SR-Edit planner emits the
// predicate-deletion edit on its own — no extra wiring needed here.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — change JOIN kind.

import type { JoinKind, QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import { commitEdit, type CommitEditCtx } from '../commitEdit';

export type ChangeJoinKindOptions = {
  /**
   * Optional structure used for index-bounds validation + same-kind no-op
   * detection. Lets the caller surface a friendlier error than SR-Edit's
   * silent drop on out-of-range, and skip a redundant commit when the kind
   * matches the current value.
   */
  readonly structure?: QueryStructure;
};

export async function changeJoinKind(
  index: number,
  newKind: JoinKind,
  ctx: CommitEditCtx,
  options: ChangeJoinKindOptions = {},
): Promise<void> {
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(`changeJoinKind: index must be a non-negative integer (got ${index})`);
  }
  if (options.structure) {
    const joins = options.structure.joins ?? [];
    if (joins.length === 0) {
      throw new Error('changeJoinKind: JOIN list is empty');
    }
    if (index >= joins.length) {
      throw new Error(
        `changeJoinKind: index ${index} out of range (JOIN list has ${joins.length} item(s))`,
      );
    }
    const current = joins[index];
    if (current?.kind === newKind) {
      return;
    }
  }
  await commitEdit({ op: 'modify-join-kind', index, newKind }, ctx);
}
